import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import type { StoreDetail, UserListItem } from '../../types/admin';
import {
  ActiveBadge,
  AdminLoading,
  AdminPageHeader,
  UserActiveBadge,
  confirmAction,
  formatDate,
} from './adminShared';
import { setSuperadminViewStore } from '../../utils/superadminStoreView';
import './AdminLayout.css';

const AdminStoreDetail: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const [store, setStore] = useState<StoreDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toggling, setToggling] = useState(false);

  const load = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.getStore(storeId);
      setStore(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error cargando tienda');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleStore = async () => {
    if (!store || !storeId) return;
    const nextActive = !store.active;
    const msg = nextActive
      ? '¿Reactivar esta tienda?'
      : 'Los usuarios no podrán acceder al dashboard. ¿Desactivar la tienda?';
    if (!confirmAction(msg)) return;

    setToggling(true);
    try {
      await adminApi.updateStore(storeId, { active: nextActive });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error actualizando tienda');
    } finally {
      setToggling(false);
    }
  };

  const handleViewDashboard = () => {
    if (!store) return;
    setSuperadminViewStore({
      storeId: store.store_id,
      storeName: store.store_name,
      storeActive: store.active,
    });
    navigate('/dashboard');
  };

  const handleToggleUser = async (user: UserListItem) => {
    const nextActive = !user.active;
    const msg = nextActive
      ? `¿Reactivar a ${user.email}?`
      : `¿Deshabilitar a ${user.email}? No podrá iniciar sesión.`;
    if (!confirmAction(msg)) return;

    try {
      await adminApi.setUserActive(user.id, nextActive);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error actualizando usuario');
    }
  };

  if (loading) return <AdminLoading />;
  if (!store) {
    return (
      <>
        <AdminPageHeader title="Tienda no encontrada" />
        {error && <div className="admin-alert admin-alert-error">{error}</div>}
        <button type="button" className="admin-btn admin-btn-secondary" onClick={() => navigate('/admin/stores')}>
          Volver
        </button>
      </>
    );
  }

  return (
    <>
      <AdminPageHeader
        title={store.store_name}
        actionLabel="+ Agregar usuario"
        actionTo={`/admin/users/new?storeId=${store.store_id}`}
      />

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      <div className="admin-card" style={{ marginBottom: 24 }}>
        <div className="admin-detail-grid">
          <div className="admin-detail-item">
            <label>Store ID</label>
            <span>{store.store_id}</span>
          </div>
          <div className="admin-detail-item">
            <label>País</label>
            <span>{store.country || '—'}</span>
          </div>
          <div className="admin-detail-item">
            <label>API Key</label>
            <span>{store.tiendanube_api_key_masked}</span>
          </div>
          <div className="admin-detail-item">
            <label>Último sync Boris</label>
            <span>{formatDate(store.last_boris_sync)}</span>
          </div>
          <div className="admin-detail-item">
            <label>Estado</label>
            <span>
              <ActiveBadge active={store.active} />
            </span>
          </div>
        </div>

        <div className="admin-toggle-row">
          <button type="button" className="admin-btn admin-btn-primary" onClick={handleViewDashboard}>
            Ver dashboard
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            onClick={handleToggleStore}
            disabled={toggling}
          >
            {store.active ? 'Desactivar tienda' : 'Activar tienda'}
          </button>
          <Link to="/admin/stores" className="admin-btn-link">
            ← Volver al listado
          </Link>
        </div>
      </div>

      <div className="admin-card">
        <h2 style={{ margin: '0 0 16px', fontSize: 16 }}>
          Usuarios ({store.users.length})
        </h2>
        {store.users.length === 0 ? (
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Sin usuarios.{' '}
            <Link to={`/admin/users/new?storeId=${store.store_id}`}>Agregar el primero</Link>
          </p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Estado</th>
                  <th>Creado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {store.users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.email}</td>
                    <td>
                      <UserActiveBadge active={user.active} />
                    </td>
                    <td>{formatDate(user.created_at)}</td>
                    <td>
                      <button
                        type="button"
                        className="admin-btn-link"
                        onClick={() => handleToggleUser(user)}
                      >
                        {user.active ? 'Deshabilitar' : 'Habilitar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminStoreDetail;
